import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Trash2, UploadCloud, Check, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import API, { formatApiError } from "@/lib/api";

const fileUrl = (f) => f.url;
const fmtSize = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`);

export default function MediaLibrary({ onSelect, selectedUrl }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(() => {
    API.get("/admin/files").then((r) => setFiles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    setUploading(true);
    let lastUrl = "";
    for (const file of list) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post("/admin/upload", fd);
        lastUrl = data.url;
      } catch (err) {
        toast.error(`${file.name}: ${formatApiError(err)}`);
      }
    }
    setUploading(false);
    e.target.value = "";
    if (lastUrl) {
      toast.success(list.length > 1 ? `${list.length} images uploaded` : "Image uploaded");
      load();
      if (onSelect) onSelect(lastUrl);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete "${f.original_filename}"? Sessions using it will lose their banner.`)) return;
    try {
      await API.delete(`/admin/files/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Image deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="media-library">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-[#5A6E63]">{files.length} image{files.length === 1 ? "" : "s"} &middot; JPG, PNG, WEBP, GIF up to 5 MB</p>
        <Button type="button" size="sm" data-testid="media-upload-btn" disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="rounded-full bg-[#2D5A47] hover:bg-[#224536] text-[#FDFBF7]">
          <UploadCloud size={14} className="mr-1.5" /> {uploading ? "Uploading..." : "Upload Images"}
        </Button>
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden" data-testid="media-file-input" onChange={upload} />
      </div>
      {loading ? (
        <p className="text-sm text-[#5A6E63] mt-6" data-testid="media-loading">Loading images...</p>
      ) : files.length === 0 ? (
        <div className="mt-6 border border-dashed border-[#2D5A47]/30 rounded-2xl p-8 text-center text-sm text-[#5A6E63]" data-testid="media-empty">
          <ImageIcon className="mx-auto mb-2 text-[#2D5A47]/50" size={28} /> No images yet. Upload your first banner.
        </div>
      ) : (
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {files.map((f) => {
            const url = fileUrl(f);
            const selected = selectedUrl === url;
            return (
              <div key={f.id} data-testid={`media-item-${f.id}`}
                className={`group relative rounded-2xl overflow-hidden border bg-[#FAF6F0] ${selected ? "border-[#D98E32] ring-2 ring-[#D98E32]/40" : "border-[#2D5A47]/15"}`}>
                <button type="button" onClick={() => onSelect && onSelect(url)} data-testid={`media-select-${f.id}`}
                  className={`block w-full ${onSelect ? "cursor-pointer" : "cursor-default"}`}>
                  <img src={url} alt={f.original_filename} className="w-full h-28 object-cover" loading="lazy" />
                </button>
                {selected && (
                  <span className="absolute top-2 left-2 bg-[#D98E32] text-white rounded-full p-1"><Check size={12} /></span>
                )}
                <button type="button" onClick={() => remove(f)} data-testid={`media-delete-${f.id}`}
                  className="absolute top-2 right-2 bg-white/90 text-red-600 rounded-full p-1.5 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
                  <Trash2 size={13} />
                </button>
                <div className="px-2.5 py-1.5 text-[11px] text-[#5A6E63] truncate" title={f.original_filename}>
                  {f.original_filename} &middot; {fmtSize(f.size)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
